import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { useDispatch } from "react-redux";
import { setAccessToken } from "../../redux/authSlice";
import { useAccessToken } from "../../hooks/useAccessToken";
import "./Authentication.scss";

/**
 * Authentication Component
 *
 * This component handles all MSAL authentication logic.
 * Users are redirected here first, authentication is performed,
 * and then they're sent to their intended destination.
 */
export default function Authentication() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { instance, accounts, inProgress } = useMsal();
  const { getAccessToken } = useAccessToken();
  
  const [sessionId] = useState(sessionStorage.getItem("sessionId") || "");
  const [authStatus, setAuthStatus] = useState("initializing");
  const loginInitiatedRef = useRef(false);

  // Get the intended destination from sessionStorage (survives redirects) or location state or default to "/"
  const intendedDestination = sessionStorage.getItem("intendedDestination") || location.state?.from || "/";

  /**
   * Validates the current user by calling the avatar backend API.
   */
  const validateUser = async (email) => {
    try {
      const token = await getAccessToken(["User.Read"]);
      const login_endpoint = process.env.REACT_APP_AVATAR_API_LOGIN_ENDPOINT;
      
      const response = await fetch(login_endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ unique_id: sessionId }),
      });

      if (response.status === 200) {
        const data = await response.json();
        
        if (data?.message === "False") {
          return false;
        }
        
        if (Array.isArray(data) && data.length > 0) {
          const userInfo = data[0];
          
          if (userInfo.UserId) {
            sessionStorage.setItem("userId", userInfo.UserId);
          }
          
          if (userInfo.Roles) {
            const rolesString = userInfo.Roles;
            const roles = rolesString
              .split(';')
              .map(role => role.replace(/^[^-]*-/, '').trim())
              .filter(Boolean);
            
            sessionStorage.setItem("userRoles", JSON.stringify(roles));
          }
          
          return true;
        }
        
        return false;
      } else {
        return false;
      }
    } catch (error) {
      console.error("User validation failed:", error);
      return false;
    }
  };

  /**
   * Calls the loadData API to initialize user-specific data.
   */
  const callLoadDataAPI = async (token) => {
    try {
      const loadDataEndpoint = process.env.REACT_APP_AVATAR_API_LOAD_DATA_ENDPOINT;

      const response = await fetch(loadDataEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ unique_id: sessionId }),
      });

      if (response.status !== 200) {
        console.warn("LoadData API returned non-200 status:", response.status);
      }
    } catch (error) {
      console.error("Error calling LoadData API:", error);
    }
  };

  /**
   * Trigger MSAL login if no accounts are found
   * Use ref to prevent multiple login calls
   */
  useEffect(() => {
    // Only proceed if no interaction is in progress and we haven't initiated login yet
    if (accounts.length === 0 && 
        inProgress === InteractionStatus.None && 
        !loginInitiatedRef.current) {
      
      console.log("No accounts found. Triggering login...");
      setAuthStatus("logging_in");
      loginInitiatedRef.current = true;
      
      instance.loginRedirect({ 
        scopes: ["User.Read"],
        prompt: "select_account"
      }).catch(error => {
        console.error("Login redirect failed:", error);
        loginInitiatedRef.current = false;
        setAuthStatus("error");
      });
    }
  }, [accounts, inProgress, instance]);

  /**
   * Once authenticated, validate user and redirect to intended destination
   */
  useEffect(() => {
    const authenticateAndRedirect = async () => {
      // Only proceed if we have accounts and no interaction is in progress
      if (accounts.length > 0 && inProgress === InteractionStatus.None) {
        try {
          setAuthStatus("validating");
          
          const account = instance.getActiveAccount() || accounts[0];
          const isValid = await validateUser(account.username);

          if (isValid) {
            setAuthStatus("loading_data");
            const token = await getAccessToken(["User.Read"]);
            dispatch(setAccessToken(token));
            console.log("Access token acquired.");
            
            await callLoadDataAPI(token);
            
            setAuthStatus("authenticated");
            
            // Clear the stored destination and redirect
            sessionStorage.removeItem("intendedDestination");
            console.log("Authentication successful. Redirecting to:", intendedDestination);
            navigate(intendedDestination, { replace: true });
          } else {
            console.warn("User is not valid. Redirecting to Access Denied.");
            setAuthStatus("access_denied");
            navigate("/access-denied", { replace: true });
          }
        } catch (err) {
          console.error("Error during authentication:", err);
          setAuthStatus("error");
        }
      }
    };

    authenticateAndRedirect();
  }, [accounts, inProgress, getAccessToken, navigate, intendedDestination, dispatch, instance]);

  // Render loading states
  const renderStatus = () => {
    // Show specific message if interaction is in progress
    if (inProgress !== InteractionStatus.None) {
      return "Authentication in progress...";
    }
    
    switch (authStatus) {
      case "initializing":
        return "Initializing...";
      case "logging_in":
        return "Redirecting to login...";
      case "validating":
        return "Validating user...";
      case "loading_data":
        return "Loading data...";
      case "authenticated":
        return "Authentication successful! Redirecting...";
      case "access_denied":
        return "Access denied. Redirecting...";
      case "error":
        return "Authentication error. Please try again.";
      default:
        return "Processing...";
    }
  };

  // Don't render anything if interaction is in startup or handle redirect state
  if (inProgress === InteractionStatus.Startup || inProgress === InteractionStatus.HandleRedirect) {
    return null;
  }

  return (
    <div className="authentication-container">
      <div className="authentication-card">
        <div className="authentication-logo">
          <div className="spinner"></div>
        </div>
        <h2 className="authentication-title">Mobility - GTM Factory</h2>
        <p className="authentication-status">{renderStatus()}</p>
      </div>
    </div>
  );
}
